import { useState, useEffect, useRef } from 'react';
import { X, Shield, BarChart2, Megaphone, ChevronDown, ChevronUp } from 'lucide-react';
import type { CookieConsentPreferences } from '../../hooks/useCookieConsent';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  currentPreferences: CookieConsentPreferences | null;
  onSave: (analytics: boolean, marketing: boolean) => void;
  onAcceptAll: () => void;
  onRefuseAll: () => void;
  onClose: () => void;
}

interface CategoryState {
  analytics: boolean;
  marketing: boolean;
}

interface AccordionState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export function CookiePreferencesModal({
  isOpen,
  currentPreferences,
  onSave,
  onAcceptAll,
  onRefuseAll,
  onClose,
}: CookiePreferencesModalProps) {
  const [categories, setCategories] = useState<CategoryState>({
    analytics: currentPreferences?.analytics ?? false,
    marketing: currentPreferences?.marketing ?? false,
  });
  const [accordion, setAccordion] = useState<AccordionState>({
    necessary: false,
    analytics: false,
    marketing: false,
  });
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCategories({
        analytics: currentPreferences?.analytics ?? false,
        marketing: currentPreferences?.marketing ?? false,
      });
      setTimeout(() => closeButtonRef.current?.focus(), 50);
    }
  }, [isOpen, currentPreferences]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  function toggleAccordion(key: keyof AccordionState) {
    setAccordion(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleCategory(key: keyof CategoryState) {
    setCategories(prev => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={modalRef}
        className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg mx-0 sm:mx-4 max-h-[90vh] flex flex-col animate-slide-up"
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 id="cookie-modal-title" className="text-lg font-semibold text-gray-900">
              Préférences de cookies
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Personnalisez vos choix par catégorie
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-5 leading-relaxed">
            Vous pouvez choisir les catégories de cookies que vous autorisez. Les cookies nécessaires au fonctionnement du site sont toujours activés. Les cookies de mesure d'audience nous aident à améliorer TrezoOne.
          </p>

          <div className="space-y-3">
            <CookieCategory
              icon={<Shield size={16} className="text-blue-600" />}
              title="Cookies nécessaires"
              description="Indispensables au fonctionnement du site et à la sécurisation de votre session."
              enabled={true}
              locked={true}
              expanded={accordion.necessary}
              onToggleExpand={() => toggleAccordion('necessary')}
            />

            <CookieCategory
              icon={<BarChart2 size={16} className="text-blue-500" />}
              title="Cookies de mesure d'audience"
              description="Permettent de mesurer la fréquentation et d'améliorer les performances du site."
              enabled={categories.analytics}
              locked={false}
              expanded={accordion.analytics}
              onToggleExpand={() => toggleAccordion('analytics')}
              onToggleEnabled={() => toggleCategory('analytics')}
            />

            <CookieCategory
              icon={<Megaphone size={16} className="text-blue-400" />}
              title="Cookies marketing"
              description="Utilisés pour personnaliser les contenus et mesurer l'efficacité des campagnes."
              enabled={categories.marketing}
              locked={false}
              expanded={accordion.marketing}
              onToggleExpand={() => toggleAccordion('marketing')}
              onToggleEnabled={() => toggleCategory('marketing')}
            />
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              En savoir plus :{' '}
              <a href="/politique-confidentialite" className="text-blue-600 hover:underline">
                Politique de confidentialité
              </a>
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2">
          <button
            onClick={onRefuseAll}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
          >
            Tout refuser
          </button>
          <button
            onClick={() => onSave(categories.analytics, categories.marketing)}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
          >
            Enregistrer mes choix
          </button>
          <button
            onClick={onAcceptAll}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
          >
            Tout accepter
          </button>
        </div>
      </div>
    </div>
  );
}

interface CookieCategoryProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  locked: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleEnabled?: () => void;
}

function CookieCategory({
  icon,
  title,
  description,
  enabled,
  locked,
  expanded,
  onToggleExpand,
  onToggleEnabled,
}: CookieCategoryProps) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50/80">
        <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-800">{title}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {locked ? (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
              Toujours actif
            </span>
          ) : (
            <button
              role="switch"
              aria-checked={enabled}
              aria-label={`${title} : ${enabled ? 'activé' : 'désactivé'}`}
              onClick={onToggleEnabled}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
                enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  enabled ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
          )}
          <button
            onClick={onToggleExpand}
            className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500"
            aria-label={expanded ? 'Réduire' : 'En savoir plus'}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 py-3 bg-white border-t border-gray-100">
          <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
        </div>
      )}
    </div>
  );
}
